import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import CoberturaNota from '../../components/CoberturaNota/CoberturaNota';
import '../ConsultoriaMicrosoftAzurePage/ConsultoriaMicrosoftAzurePage.css';

const PAGE_URL = 'https://e-asy.es/servicios/business-central';
const HUB_URL = '/servicios/consultoria-microsoft-azure';

const FAQ_ITEMS = [
  {
    question: '¿Sois partner de Dynamics 365 Business Central?',
    answer:
      'Estamos en el roadmap de Microsoft Solutions Partner. Lo decimos con transparencia: es una especialización en curso, no un sello cerrado. Implantamos y conectamos Business Central hoy, sin vender el badge.',
  },
  {
    question: '¿Business Central o Vulpin?',
    answer:
      'BC cuando el ERP tiene que vivir en el entorno Microsoft y hablar con 365. Vulpin cuando el núcleo es sociosanitario (menores, dependencia, salud) o un expediente de categoría especial. No empujamos BC por catálogo; si Vulpin encaja mejor, te lo decimos.',
  },
  {
    question: '¿Implantáis cualquier ERP, o solo Business Central?',
    answer:
      'Esta ficha es Dynamics 365 Business Central. Vulpin Suite es nuestro ERP modular, sobre todo sociosanitario. Evaluamos BC o Vulpin según el caso; no empujamos uno por catálogo.',
  },
  {
    question: '¿Migráis desde Excel u otro ERP?',
    answer:
      'Sí, cuando tiene sentido. Evaluación, datos, implantación por fases y formación al equipo que facturará y comprará de verdad. Sin un “big bang” de seis meses que nadie opera.',
  },
  {
    question: '¿Encaja con Kit Digital o Kit Consulting?',
    answer:
      'Sí, si la ayuda cubre consultoría o implantación de gestión. El bono no es el ERP: aquí aterrizamos Business Central (o Vulpin, si encaja mejor) en un plan posterior a la justificación.',
  },
];

const BusinessCentralPage = ({ darkMode, toggleDarkMode }) => {
  const bloques = [
    {
      titulo: 'Evaluación honesta',
      descripcion:
        'BC, Vulpin u otra base. El criterio es el proceso y el dato, no el partnership.',
      puntos: [
        'Qué ya usáis (Excel, 365, otro ERP) y qué duele de verdad',
        'Si el expediente es sociosanitario, partimos de Vulpin',
        'Alcance por fases: lo que el equipo puede operar el lunes',
      ],
    },
    {
      titulo: 'Implantación y conexión Microsoft',
      descripcion:
        'Business Central hablando con 365 y con el resto de la operación. No un silo más.',
      puntos: [
        'Configuración alineada a facturación, compras y tesorería reales',
        'Identidad y permisos del entorno Microsoft',
        'Integraciones cuando hay tienda, almacén u otro sistema',
      ],
    },
    {
      titulo: 'Formación y operación',
      descripcion:
        'El equipo que factura y compra es quien tiene que usarlo. Nos quedamos al otro lado.',
      puntos: [
        'Formación a quien trabaja con el ERP, no solo a dirección',
        'Criterio de qué se automatiza y qué se deja manual',
        'Misma línea que Azure y 365 si el equipo tecnológico es externo',
      ],
    },
  ];

  const paraQuien = [
    {
      titulo: 'Pyme que ya vive en Microsoft 365',
      descripcion:
        'Correo y archivos en 365, la gestión en Excel o en un programa que no habla con nadie. BC encaja cuando el ERP tiene que estar en esa casa.',
    },
    {
      titulo: 'Quien llega con un “hay que poner Dynamics”',
      descripcion:
        'A veces el fabricante o un auditor lo pide. Revisamos si BC es la pieza o si estáis comprando un nombre. Si Vulpin cubre mejor el caso, te lo decimos.',
    },
    {
      titulo: 'No sois una entidad sociosanitaria',
      descripcion:
        'Si el núcleo es menores, dependencia o salud, esta no es la ficha de entrada: es Vulpin. BC no se cuela por inercia.',
    },
  ];

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      url: PAGE_URL,
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    }),
    []
  );

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Implantación Dynamics 365 Business Central',
    description:
      'Implantación de Microsoft Dynamics 365 Business Central para pymes: evaluación frente a Vulpin u otra base, conexión con Microsoft 365, formación y operación. Roadmap Microsoft Solutions Partner en curso.',
    url: PAGE_URL,
    serviceType: 'Implantación ERP Dynamics 365 Business Central',
    provider: {
      '@type': 'Organization',
      name: 'E-ASY',
      url: 'https://e-asy.es',
    },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Canarias' },
      { '@type': 'Country', name: 'España' },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://e-asy.es/' },
      { '@type': 'ListItem', position: 2, name: 'Servicios', item: 'https://e-asy.es/servicios' },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Consultoría Microsoft, Azure y partners',
        item: 'https://e-asy.es/servicios/consultoria-microsoft-azure',
      },
      {
        '@type': 'ListItem',
        position: 4,
        name: 'Business Central',
        item: PAGE_URL,
      },
    ],
  };

  return (
    <>
      <SEO
        title="Implantación Dynamics 365 Business Central | E-ASY"
        description="Consultor Business Central para pymes: implantación Dynamics 365 BC, conexión con Microsoft 365 y formación. Si Vulpin encaja mejor, te lo decimos. Proyectos en remoto en toda España, con sede en Tenerife."
        keywords="consultor Business Central Canarias, implantación Dynamics 365 Business Central, partner BC pymes España, migrar a Business Central, ERP Microsoft 365, E-ASY Tenerife"
        canonical={PAGE_URL}
      />
      <JsonLd id="jsonld-bc-faq" data={faqSchema} />
      <JsonLd id="jsonld-bc-service" data={serviceSchema} />
      <JsonLd id="jsonld-bc-breadcrumb" data={breadcrumbSchema} />
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      <div className={`partners-cloud-page ${darkMode ? 'dark' : 'light'}`}>
        <section className="partners-hero">
          <div className="partners-hero-content">
            <div className="partners-hero-text">
              <h1 className="partners-hero-title">
                Implantación Dynamics 365 Business Central
              </h1>
              <p className="partners-hero-subtitle">
                <strong>Consultores Business Central</strong> para pymes que
                necesitan el ERP en el entorno Microsoft, hablando con 365 — no
                un silo más ni un partner que configura y desaparece.
              </p>
              <p className="partners-hero-subtitle">
                Si el caso es sociosanitario, no empujamos BC:{' '}
                <Link to="/vulpin">Vulpin Suite</Link> es la base. Roadmap de
                Microsoft Solutions Partner <strong>en curso</strong>; el
                trabajo de implantación, no el sello.
              </p>
              <div className="partners-hero-cta">
                <Link to="/contacto" className="partners-hero-button primary">
                  Agenda una consultoría gratuita
                </Link>
                <Link to={HUB_URL} className="partners-hero-button secondary">
                  Ver el hub Microsoft
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="partners-que-es">
          <div className="partners-que-es-content">
            <div className="partners-que-es-header">
              <h2 className="partners-que-es-title">BC no es Vulpin</h2>
              <p className="partners-que-es-subtitle">
                <strong>Business Central</strong> es el ERP de Microsoft:
                facturación, compras, tesorería e inventario en la misma casa
                que el correo y los archivos. <strong>Vulpin</strong> es
                nuestro ERP modular para pymes y entidades que tratan datos de
                categoría especial. La evaluación es el primer entregable, no
                un trámite para vender licencias.
              </p>
              <p className="partners-que-es-subtitle">
                Azure (infra de proyectos) y 365 (día a día) están en el{' '}
                <Link to={HUB_URL}>hub Microsoft</Link>
                {' '}y en la ficha de{' '}
                <Link to="/servicios/azure">consultoría Azure</Link>. Esta
                página es solo el ERP Dynamics.
              </p>
            </div>
          </div>
        </section>

        <section className="partners-servicios">
          <div className="partners-servicios-content">
            <div className="partners-servicios-header">
              <h2 className="partners-servicios-title">Cómo implantamos BC</h2>
              <p className="partners-servicios-subtitle">
                Por fases, con el equipo que lo usará. Sin un proyecto eterno
                que nadie puede operar.
              </p>
            </div>
            <div className="partners-servicios-grid partners-paquetes-grid">
              {bloques.map((item) => (
                <article key={item.titulo} className="partners-servicio-card">
                  <h3 className="servicio-title">{item.titulo}</h3>
                  <p className="servicio-description">{item.descripcion}</p>
                  <div className="servicio-casos">
                    <h4 className="casos-title">Incluye:</h4>
                    <ul className="casos-list">
                      {item.puntos.map((punto) => (
                        <li key={punto} className="caso-item">
                          {punto}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-sectores">
          <div className="partners-sectores-content">
            <div className="partners-sectores-header">
              <h2 className="partners-sectores-title">Para quién</h2>
              <p className="partners-sectores-subtitle">
                Pymes en Canarias y el resto de España. Si el núcleo es ONG o
                centro sociosanitario, id a{' '}
                <Link to="/vulpin">Vulpin</Link> o a{' '}
                <Link to="/erp-para-ong">software de gestión para ONG</Link>.
              </p>
            </div>
            <div className="partners-sectores-grid">
              {paraQuien.map((item) => (
                <article key={item.titulo} className="partners-sector-card">
                  <h3 className="sector-title">{item.titulo}</h3>
                  <p className="sector-description">{item.descripcion}</p>
                </article>
              ))}
            </div>
            <p className="partners-ciber-callout">
              Ficha hija de{' '}
              <Link to={HUB_URL}>
                Consultoría Microsoft / Azure / Google Workspace
              </Link>
              . El equipo tecnológico externo cubre 365, Azure y BC; aquí
              está el detalle del ERP Dynamics. Lectura:{' '}
              <Link to="/blog/business-central-o-vulpin-como-se-decide">
                Business Central o Vulpin: cómo se decide
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="partners-faq">
          <div className="partners-faq-content">
            <div className="partners-faq-header">
              <h2 className="partners-faq-title">Preguntas frecuentes</h2>
            </div>
            <div className="partners-faq-list">
              {FAQ_ITEMS.map((item) => (
                <details key={item.question} className="partners-faq-item">
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="partners-cta">
          <div className="partners-cta-content">
            <h2 className="partners-cta-title">
              ¿Business Central es la pieza, o hay que mirar otra base?
            </h2>
            <p className="partners-cta-subtitle">
              Lo vemos con vuestros procesos. Si no encaja, te lo decimos
              antes de montar licencias.
            </p>
            <div className="partners-cta-buttons">
              <Link to="/contacto" className="partners-cta-button primary">
                Agenda una consultoría gratuita
              </Link>
              <Link to="/vulpin" className="partners-cta-button secondary">
                Ver Vulpin Suite
              </Link>
            </div>
          </div>
        </section>
      </div>
      <CoberturaNota />
      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </>
  );
};

export default BusinessCentralPage;
